import React from 'react';

import { values } from 'lodash-es';
import { useRecoilState } from 'recoil';

import { acWarn } from '@looko-corp/acloset-react-kit/src/sentry';

import { useGetShops } from '@/app/manager/hooks/useGetShops';
import { useShopPlansDetail } from '@/app/plan/services';
import useGetShopInfo from '@/app/shop/shop-info/useGetShopInfo';
import { useUserAuth } from '@/state/cookieContext';
import { shopAuthContextState } from '@/state/userAuthContext';

import { ShopSideEffects } from './ShopSideEffects';
import { ShopInfoContext, ShopPlanContext } from './hooks';

/**
 * 선택한 샵 정보를 주입합니다.
 *
 * shopAuthContextState 으로부터 샵을 스위칭 합니다.
 */
const ShopInfoProvider = ({ children }: { children: React.ReactNode }) => {
  const userAuth = useUserAuth();

  const isOwner = userAuth?.roles.includes('ROLE_OWNER') || userAuth?.roles.includes('ROLE_GUEST');

  const [shopAuth, setShopAuth] = useRecoilState(shopAuthContextState);

  const { data: shopsList, isFetched } = useGetShops(undefined, { enabled: isOwner });

  const shopID = !isOwner || isFetched ? shopAuth.shopID : '';

  const shopInfoResult = useGetShopInfo(shopID);

  const {
    data: shopPlanResult,
    refetch: shopPlanResultRefetch,
    isLoading: shopPlanResultIsLoading,
    isError: shopPlanResultIsError,
  } = useShopPlansDetail(shopID);

  // XXX: 권한이 없는 샵을 선택하고 있는 경우, 첫번째 샵을 선택합니다.
  React.useEffect(() => {
    if (isOwner && !shopsList?.shops?.some(v => v.shopID === shopAuth.shopID)) {
      acWarn('권한이 없는 샵을 선택하고 있습니다. 첫번째 샵을 선택합니다.');
      setShopAuth({ shopID: shopsList?.shops?.[0]?.shopID ?? '' });
    }
  }, [isOwner, setShopAuth, shopAuth.shopID, shopsList?.shops]);

  const shopInfoData = React.useMemo(() => {
    if (!shopInfoResult.data) {
      return null;
    }

    return {
      shopInfo: {
        ...shopInfoResult.data,
        서비스_사용에_따른_설정값: {
          enableDetailImage: shopInfoResult.data.enableDetailImage,
          enableBackgroundRemoved: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-REMOVE-BACKGROUND')
            ? shopInfoResult.data.enableBackgroundRemoved
            : false,
          marginAdjust: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-REMOVE-BACKGROUND')
            ? values(shopInfoResult.data.imageCropOption).includes(true)
            : false,

          enableAutoGenerateTitle: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION')
            ? shopInfoResult.data.enableAutoGenerateTitle
            : false,

          imageFilterEnabled: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_FILTER-IMAGE')
            ? (shopInfoResult.data.imageRetouchingOption?.enableImageRetouchingOption ?? false)
            : false,

          enableUploadImageRatio: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_RESIZE-IMAGE')
            ? (shopInfoResult.data.platformImageOption?.enableUploadImageRatio ?? false)
            : false,
          enableAutoPrice: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION')
            ? shopInfoResult.data.enableAutoPrice
            : false,
          enableSearchKeywordTemplate: shopPlanResult?.extendedPlan.enabled(
            'PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION',
          )
            ? shopInfoResult.data.enableSearchKeywordTemplate
            : false,

          enableAutoSizeAssignment: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION')
            ? shopInfoResult.data.enableAutoSizeAssignment
            : false,

          enableAutoFabricClassification: shopPlanResult?.extendedPlan.enabled(
            'PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION',
          )
            ? shopInfoResult.data.enableAutoFabricClassification
            : false,

          enableAutoRotateImage: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-ROTATE-IMAGE')
            ? (shopInfoResult.data.enableAutoRotateImage ?? false)
            : false,

          isAutoInputOrigin: shopPlanResult?.extendedPlan.enabled('PLAN_ACTION_AUTO-INPUT-PRODUCT-INFORMATION')
            ? (shopInfoResult.data.originSetting?.isAutoInputOrigin ?? false)
            : false,
        },
      },
      refetch: shopInfoResult.refetch,
    };
  }, [shopInfoResult.data, shopInfoResult.refetch, shopPlanResult]);

  const shopPlanData = React.useMemo(() => {
    if (!shopPlanResult) {
      return null;
    }

    return {
      shopPlan: shopPlanResult.extendedPlan,
      refetch: shopPlanResultRefetch,
    };
  }, [shopPlanResult, shopPlanResultRefetch]);

  if (!shopID || shopInfoResult.isLoading || shopPlanResultIsLoading) {
    return null; // 로딩 표시
  }

  if (shopInfoResult.isError || shopPlanResultIsError) {
    return null; // 에러 표시
  }

  if (shopInfoData == null || shopPlanData == null) {
    return null;
  }

  return (
    <ShopInfoContext.Provider value={shopInfoData}>
      <ShopPlanContext.Provider value={shopPlanData}>
        <ShopSideEffects />
        {children}
      </ShopPlanContext.Provider>
    </ShopInfoContext.Provider>
  );
};

export default ShopInfoProvider;
